import type { Pead, ScreenerData } from "epsm-api-types/models";
import { useMemo } from "react";
import { formatValueNumber } from "@/components/widgets/helpers/FormattingHelpers";
import { componentClassNames } from "@/components/widgets/Quote/config";
import { QuoteRow } from "@/components/widgets/Quote/ui/components";
import { LineHeader } from "@/components/widgets/Quote/ui/components/LineHeader";
import { formatPositivePercentValue } from "../../../lib/helpers/formatPositivePercentValue";
import { getLatestPrice } from "../../../lib/helpers/getLatestPrice";

export const PriceDriftTable = ({
	screenerData,
	peadData,
}: {
	screenerData: ScreenerData | null;
	peadData: Pead | null;
}) => {
	const { drift_name } = screenerData ?? {};
	const { upside } = peadData ?? {};

	const { highPrice, meanPrice, lowPrice } = useMemo(
		() => ({
			highPrice: getLatestPrice(peadData?.max),
			meanPrice: getLatestPrice(peadData?.avg),
			lowPrice: getLatestPrice(peadData?.min),
		}),
		[peadData],
	);

	const hasAnyData = useMemo(
		() =>
			drift_name != null ||
			upside != null ||
			highPrice != null ||
			meanPrice != null ||
			lowPrice != null,
		[drift_name, upside, highPrice, meanPrice, lowPrice],
	);

	if (!hasAnyData) return null;

	return (
		<div className={componentClassNames} data-testid="price-drift-table">
			<LineHeader title="Price Drift" />

			{drift_name && <QuoteRow left={drift_name} />}
			{upside != null && (
				<QuoteRow
					left="Upside"
					right={formatPositivePercentValue(peadData?.upside)}
				/>
			)}
			{highPrice != null && (
				<QuoteRow left="High Price" right={formatValueNumber(highPrice)} />
			)}
			{meanPrice != null && (
				<QuoteRow left="Mean Price" right={formatValueNumber(meanPrice)} />
			)}
			{lowPrice != null && (
				<QuoteRow left="Low Price" right={formatValueNumber(lowPrice)} />
			)}
		</div>
	);
};
